// components/interactive-heatmap/InteractiveHeatmap.js
import React, { useMemo, useState } from "react";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";

import "./InteractiveHeatmap.scss";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getMonthStart = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const defaultColorScale = (value) => {
  const safe = Math.max(0, Math.min(1, Number(value) || 0));

  if (safe <= 0) return "var(--border2)";
  if (safe <= 0.2) return "#95BBD2";
  if (safe <= 0.4) return "#6FA2C3";
  if (safe <= 0.6) return "#4889B1";
  if (safe <= 0.8) return "#3c7ca3";

  return "#2370A1";
};

export default function InteractiveHeatmap({
  data = [],
  onCellPress,
  colorScale = defaultColorScale,
  initialDate = new Date(),
  futureMonths = 2,
  getValue = (item) => item?.checked ?? item?.count ?? 0,
}) {
  const today = new Date();

  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth());
  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());

  const dataMap = useMemo(
    () =>
      new Map(
        data.filter((item) => item?.date).map((item) => [item.date, item]),
      ),
    [data],
  );

  const earliestDate = useMemo(() => {
    const dates = data
      .map((item) => new Date(`${item?.date}T00:00:00`))
      .filter((date) => !Number.isNaN(date.getTime()));

    return dates.length
      ? new Date(Math.min(...dates.map((date) => date.getTime())))
      : today;
  }, [data]);

  const currentMonthDate = new Date(currentYear, currentMonth, 1);

  const earliestMonthDate = getMonthStart(earliestDate);

  const maxMonthDate = new Date(
    today.getFullYear(),
    today.getMonth() + futureMonths,
    1,
  );

  const isPreviousDisabled = currentMonthDate <= earliestMonthDate;

  const isNextDisabled = currentMonthDate >= maxMonthDate;

  const goToPreviousMonth = () => {
    if (isPreviousDisabled) return;

    const previous = new Date(currentYear, currentMonth - 1, 1);

    setCurrentMonth(previous.getMonth());
    setCurrentYear(previous.getFullYear());
  };

  const goToNextMonth = () => {
    if (isNextDisabled) return;

    const next = new Date(currentYear, currentMonth + 1, 1);

    setCurrentMonth(next.getMonth());
    setCurrentYear(next.getFullYear());
  };

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  const firstDay = new Date(currentYear, currentMonth, 1).getDay();

  const cells = [];

  for (let index = 0; index < 42; index++) {
    const day = index - firstDay + 1;

    if (day < 1 || day > daysInMonth) {
      cells.push(
        <div
          key={`empty-${index}`}
          className="interactive-heatmap__cell interactive-heatmap__cell--empty"
        />,
      );

      continue;
    }

    const date = new Date(currentYear, currentMonth, day);

    const dateStr = formatDate(date);
    const item = dataMap.get(dateStr);
    const value = item ? getValue(item) : 0;
    const isToday = dateStr === formatDate(today);

    cells.push(
      <button
        key={dateStr}
        type="button"
        className={`interactive-heatmap__cell ${
          isToday ? "interactive-heatmap__cell--today" : ""
        }`}
        style={{
          backgroundColor: colorScale(value),
        }}
        onClick={() => onCellPress?.(dateStr, value, item)}
        disabled={!onCellPress}
        title={dateStr}
      >
        {day}
      </button>,
    );
  }

  return (
    <div className="interactive-heatmap">
      <div className="interactive-heatmap__header">
        <button
          type="button"
          className="interactive-heatmap__nav"
          onClick={goToPreviousMonth}
          disabled={isPreviousDisabled}
          aria-label="Previous month"
        >
          <IoChevronBack />
        </button>

        <strong className="interactive-heatmap__month">
          {currentMonthDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </strong>

        <button
          type="button"
          className="interactive-heatmap__nav"
          onClick={goToNextMonth}
          disabled={isNextDisabled}
          aria-label="Next month"
        >
          <IoChevronForward />
        </button>
      </div>

      <div className="interactive-heatmap__weekdays">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div className="interactive-heatmap__grid">{cells}</div>
    </div>
  );
}
